/**
 * Converts an observable to a promise.
 *
 * @remarks
 *
 * The promise resolves when the observable emits a value that satisfies
 * the `shouldResolve` function or when the observable completes with a value.
 *
 * The promise rejects if the:
 *
 * 1. Observable completes without a value
 * 2. Observable emits an error
 * 3. AbortSignal is aborted
 * 4. Operation times out
 *
 * By default, the promise resolves with the first emitted value.
 *
 * @internal scope: package
 */
export class ObservablePromise {
    #deferred = Promise.withResolvers();
    #observable;
    #shouldResolve;
    #subscription;
    #timeoutId;
    #latestValue;
    get promise() {
        return this.#deferred.promise;
    }
    constructor(observable, options) {
        this.#observable = observable;
        const { shouldResolve, signal, timeout } = options ?? {};
        this.#shouldResolve = shouldResolve ?? (() => true);
        this.#subscription = this.#observable.subscribe(this);
        signal?.addEventListener("abort", () => {
            this.#handleError(new Error("Promise was aborted."));
        });
        if (timeout !== undefined) {
            this.#timeoutId = setTimeout(() => {
                this.#handleError(new Error("Promise timed out."));
            }, timeout);
        }
    }
    next(value) {
        this.#latestValue = { value };
        try {
            if (this.#shouldResolve(value)) {
                this.#handleResolve(value);
            }
        }
        catch (error) {
            this.#handleError(error);
        }
    }
    complete() {
        if (this.#latestValue) {
            this.#handleResolve(this.#latestValue.value);
        }
        else {
            this.#handleError(new Error("Promise was completed without a value."));
        }
    }
    #teardown() {
        this.#subscription.unsubscribe();
        clearTimeout(this.#timeoutId);
    }
    #handleResolve(value) {
        this.#teardown();
        this.#deferred.resolve(value);
    }
    #handleError(error) {
        this.#teardown();
        this.#deferred.reject(error);
    }
}
